// External Golf Course API Integration
// Using Golf Course API: https://api.golfcourseapi.com/docs/api/

const GOLF_API_KEY = import.meta.env.VITE_GOLF_API_KEY || 'C57XYGUUGK5NPFZM2I47PUYE6Y';
const GOLF_API_BASE = 'https://api.golfcourseapi.com/v1';

export interface ExternalCourse {
  id: string
  name: string
  city: string
  state: string
  country: string
  zip?: string
  phone?: string
  website?: string
  holes?: number
  par?: number
  yardage?: number
  address?: string
}

export interface CourseHoleData {
  holeNumber: number
  par: number
  yardage: number
  handicap: number
}

// Search for golf courses using Golf Course API
// API Docs: https://api.golfcourseapi.com/docs/api/
// Note: API only supports search by course/club name or city
export async function searchGolfCourses(
  query: string
): Promise<ExternalCourse[]> {
  // If no API key, return demo data
  if (!GOLF_API_KEY) {
    console.log('No API key found, using demo data');
    return getDemoCourses(query);
  }

  try {
    // API endpoint: /v1/search with search_query parameter
    // Per OpenAPI spec, searches by course/club name
    const url = new URL(`${GOLF_API_BASE}/search`);
    
    const searchQuery = query.trim() || 'golf';
    url.searchParams.append('search_query', searchQuery);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Key ${GOLF_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`API error: ${response.status}`);
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    // Response format per spec: { courses: [...] }
    const courses = mapApiResponse(data);
    
    // Return results (API does its own relevance ranking)
    return courses;
  } catch (error) {
    console.error('External API error:', error);
    // Fallback to demo data
    return getDemoCourses(query);
  }
}

// Get detailed course information including hole-by-hole data
export async function getCourseDetails(courseId: string): Promise<CourseHoleData[]> {
  // If no API key, return default 18-hole layout
  if (!GOLF_API_KEY) {
    return getDefaultHoleLayout();
  }

  try {
    const response = await fetch(
      `${GOLF_API_BASE}/courses/${courseId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Key ${GOLF_API_KEY}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      console.error(`API error: ${response.status}`);
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return mapHoleData(data);
  } catch (error) {
    console.error('External API error:', error);
    return getDefaultHoleLayout();
  }
}

// Map Golf Course API response to our format
function mapApiResponse(data: any): ExternalCourse[] {
  // Handle different response structures - API returns { courses: [...] } per spec
  const courses = data.courses || data.data || data;
  
  if (!Array.isArray(courses)) {
    console.error('Unexpected API response format:', data);
    return [];
  }
  
  return courses.map((course: any) => {
    // Calculate total par and yardage from tees if available
    let totalPar = 72; // default
    let totalYardage = 0;
    let totalHoles = 18;
    
    // Try to get data from male tees (usually championship tees)
    const maleTees = course.tees?.male?.[0] || course.tees?.female?.[0];
    if (maleTees) {
      totalPar = maleTees.par_total || maleTees.holes?.length || 18;
      totalYardage = maleTees.total_yards || 0;
      totalHoles = maleTees.number_of_holes || maleTees.holes?.length || 18;
    }
    
    return {
      id: String(course.id),
      name: course.course_name || course.club_name || course.name,
      city: course.location?.city || course.city || '',
      state: course.location?.state || course.state || '',
      country: course.location?.country || course.country || 'USA',
      zip: course.location?.zip || course.zip || '',
      phone: course.phone || '',
      website: course.website || '',
      holes: totalHoles,
      par: totalPar,
      yardage: totalYardage,
      address: course.location?.address || `${course.location?.city || ''}, ${course.location?.state || ''}`
    };
  });
}

// Map hole data from Golf Course API
function mapHoleData(data: any): CourseHoleData[] {
  // The API returns { course: { tees: { male: [...], female: [...] } } }
  const courseData = data.course || data;
  
  let holes: any[] = [];
  
  // Try to get holes from male tees first (typically championship tees)
  if (courseData.tees?.male && courseData.tees.male.length > 0) {
    holes = courseData.tees.male[0].holes || [];
  } 
  // Fallback to female tees
  else if (courseData.tees?.female && courseData.tees.female.length > 0) {
    holes = courseData.tees.female[0].holes || [];
  }
  
  if (!holes || holes.length === 0) {
    console.log('No hole data found in API response, using default layout');
    return getDefaultHoleLayout();
  }
  
  // Map the holes data
  const mappedHoles = holes.map((hole: any, index: number) => ({
    holeNumber: index + 1,
    par: hole.par || 4,
    yardage: hole.yardage || 350,
    handicap: hole.handicap || index + 1
  }));
  
  // Ensure we have exactly 18 holes
  if (mappedHoles.length < 18) {
    const defaultHoles = getDefaultHoleLayout();
    return defaultHoles.map((defaultHole, idx) => 
      mappedHoles[idx] || defaultHole
    );
  }
  
  return mappedHoles.slice(0, 18);
}

// Demo/fallback data for Kentucky courses (no API key needed)
function getDemoCourses(query: string): ExternalCourse[] {
  const demoCourses: ExternalCourse[] = [
    {
      id: 'demo-valhalla',
      name: 'Valhalla Golf Club',
      city: 'Louisville',
      state: 'KY',
      country: 'USA',
      phone: '(502) 245-4475',
      website: 'https://www.valhalla.com',
      holes: 18,
      par: 72,
      yardage: 7542,
      address: '15503 Shelbyville Rd, Louisville, KY 40245'
    },
    {
      id: 'demo-keeneland',
      name: 'Keeneland Golf Club',
      city: 'Nicholasville',
      state: 'KY',
      country: 'USA',
      phone: '(859) 885-0100',
      website: 'https://www.keenelandgolf.com',
      holes: 18,
      par: 72,
      yardage: 6804,
      address: '3950 Keeneland Blvd, Nicholasville, KY 40356'
    },
    {
      id: 'demo-cherry-blossom',
      name: 'Cherry Blossom Golf Club',
      city: 'Georgetown',
      state: 'KY',
      country: 'USA',
      phone: '(502) 570-9849',
      website: 'https://www.cherryblossomgc.com',
      holes: 18,
      par: 72,
      yardage: 7127,
      address: '1901 Cherry Blossom Way, Georgetown, KY 40324'
    },
    {
      id: 'demo-gibson-bay',
      name: 'Gibson Bay Golf Course',
      city: 'Richmond',
      state: 'KY',
      country: 'USA',
      phone: '(859) 623-0225',
      website: 'https://www.gibsonbay.com',
      holes: 18,
      par: 72,
      yardage: 6851,
      address: '2000 Gibson Bay Drive, Richmond, KY 40475'
    },
    {
      id: 'demo-crooked-creek',
      name: 'Crooked Creek Golf Club',
      city: 'London',
      state: 'KY',
      country: 'USA',
      phone: '(606) 877-1993',
      holes: 18,
      par: 72,
      yardage: 6605,
      address: '550 Clubhouse Drive, London, KY 40744'
    },
    {
      id: 'demo-lassing-pointe',
      name: 'Lassing Pointe Golf Course',
      city: 'Union',
      state: 'KY',
      country: 'USA',
      phone: '(859) 384-3434',
      holes: 18,
      par: 71,
      yardage: 6765,
      address: '2266 Double Eagle Drive, Union, KY 41091'
    },
    {
      id: 'demo-heritage-hill',
      name: 'Heritage Hill Golf Course',
      city: 'Shepherdsville',
      state: 'KY',
      country: 'USA',
      phone: '(502) 543-4323',
      holes: 18,
      par: 72,
      yardage: 6748,
      address: '2200 Pee Wee Reese Road, Shepherdsville, KY 40165'
    }
  ];

  // Simple filtering by query
  if (!query) return demoCourses;
  
  const lowerQuery = query.toLowerCase();
  return demoCourses.filter(course => 
    course.name.toLowerCase().includes(lowerQuery) ||
    course.city.toLowerCase().includes(lowerQuery)
  );
}

// Default hole layout (standard par 72 course)
function getDefaultHoleLayout(): CourseHoleData[] {
  return [
    // Front 9
    { holeNumber: 1, par: 4, yardage: 380, handicap: 7 },
    { holeNumber: 2, par: 5, yardage: 520, handicap: 3 },
    { holeNumber: 3, par: 3, yardage: 165, handicap: 17 },
    { holeNumber: 4, par: 4, yardage: 395, handicap: 9 },
    { holeNumber: 5, par: 4, yardage: 410, handicap: 1 },
    { holeNumber: 6, par: 3, yardage: 185, handicap: 15 },
    { holeNumber: 7, par: 4, yardage: 425, handicap: 5 },
    { holeNumber: 8, par: 5, yardage: 545, handicap: 11 },
    { holeNumber: 9, par: 4, yardage: 390, handicap: 13 },
    // Back 9
    { holeNumber: 10, par: 4, yardage: 385, handicap: 8 },
    { holeNumber: 11, par: 5, yardage: 530, handicap: 4 },
    { holeNumber: 12, par: 3, yardage: 175, handicap: 16 },
    { holeNumber: 13, par: 4, yardage: 405, handicap: 10 },
    { holeNumber: 14, par: 4, yardage: 420, handicap: 2 },
    { holeNumber: 15, par: 3, yardage: 195, handicap: 14 },
    { holeNumber: 16, par: 4, yardage: 435, handicap: 6 },
    { holeNumber: 17, par: 5, yardage: 555, handicap: 12 },
    { holeNumber: 18, par: 4, yardage: 400, handicap: 18 }
  ];
}

